import { Component, OnInit, OnDestroy, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { defer, Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { CategoryResponse } from '../../../_models/category';
import { CourseService } from '../../../_services/course.service';
import { GlobalService } from '../../../global/global.service';
import {
  requiredFileType,
  ResponseStructure,
  StaffStore,
} from '@iq-central-front-end/share';
import Swal from 'sweetalert2';
import { CourseResponseDTO } from '../../../_models/course';
import { AuthService } from '../../../_services/auth.service';
import { finalize } from 'rxjs/operators';
import { StaffService } from '../../../_services/staff.service';

@Component({
  selector: 'iq-central-front-end-create-course',
  templateUrl: './create-course.component.html',
  styleUrls: ['./create-course.component.scss'],
})
export class CreateCourseComponent implements OnInit, OnDestroy {
  config: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '15rem',
    minHeight: '5rem',
    placeholder: 'Enter text here...',
    translate: 'no',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Arial',
    toolbarHiddenButtons: [['bold']],
    customClasses: [
      {
        name: 'quote',
        class: 'quote',
      },
      {
        name: 'redText',
        class: 'redText',
      },
      {
        name: 'titleText',
        class: 'titleText',
        tag: 'h1',
      },
    ],
  };

  sub: Subscription = new Subscription();
  currentPage = 'create-course';
  selected = '';
  createCourseForm: UntypedFormGroup;
  categories: CategoryResponse[] = [];
  subCategories: any[] = [];
  topics: any[] = [];
  userType: any;
  isLoading: boolean;

  topicsObjectArray: any[] = [];
  courseCode: string;
  courseData: CourseResponseDTO;
  fileName: string;
  courseDuration: any;
  selectedTopics: any[] = [];
  isFetching: boolean = false;
  isFetchingSubcategories: boolean = false;
  staffs: any[] = [];

  constructor(
    private readonly router: Router,
    private readonly fb: UntypedFormBuilder,
    private readonly _course: CourseService,
    private readonly _global: GlobalService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly _staffService: StaffService,
    public readonly _staffStore: StaffStore
  ) {
    this.userType = authService.getUserType();
    this.subscribeToStoreChanges();
  }

  ngOnInit(): void {
    this.courseCode = this.activatedRoute.snapshot.paramMap.get('code');

    if (this.courseCode) {
      this.getCourseData();
    }

    this.initCourseForm();
    this.getAllCategories();
    this.getStaffs();
  }

  listenToActivatedRoute() {
    this.sub.add(this.activatedRoute.data.subscribe((res) => { }));
  }

  subscribeToStoreChanges() {
    effect(() => {
      const staffsData = this._staffStore.staffs$();

      if (staffsData) {
        this.staffs = staffsData.data;
      }
    });
  }

  initCourseForm() {
    this.createCourseForm = this.fb.group({
      // this.courseData?.certificateFileUrl ?? null,
      //   [requiredFileType('pdf')],
      certificate: [null],
      picture: [
        this.courseData?.imageUrl ?? null,
        [Validators.required, requiredFileType('png', 'jpeg', 'jpg')],
      ],
      id: [this.courseData?.category?.id ?? null],
      category: [this.courseData?.subCategory?.id ?? null],
      topics: [this.selectedTopics],
      description: [this.courseData?.description ?? '', Validators.required],
      duration: [''],
      hasCertificate: [true],
      level: [this.courseData?.level ?? 'BASIC', Validators.required],
      title: [this.courseData?.title ?? '', Validators.required],
      certificateType: ['GENERIC'],
      courseCode: [this.courseData?.code],
      adminInstructorId: [this.courseData?.adminInstructor?.userId ?? null],
    });
  }

  goToChoosenRoute(route) {
    this.router.navigate([route]);
  }

  getCourseData() {
    this.isFetching = true;

    this.sub.add(
      this._course
        .getCourseById(this.courseCode)
        .pipe(finalize(() => (this.isFetching = false)))
        .subscribe({
          next: (res) => {
            this.courseData = res['responseResult'];

            if (this.courseData?.category) {
              this.getAllSubCategories(this.courseData?.category);
            }

            this.topics = this.courseData['topics'];
            this.topics.forEach((t) => {
              this.selectedTopics.push(t.id);
            });

            this.getFileName(this.courseData.imageUrl);
            // this.parseToDate(this.courseData.duration);

            this.initCourseForm();
          },
        })
    );
  }

  getStaffs() {
    this._staffStore.setPageSize(100);

    this.sub.add(
      defer(() => this._staffService.getAllEnterpriseUsers()).subscribe()
    )
  }

  getAllCategories() {
    this.sub.add(
      this._course.getAllCategories().subscribe({
        next: (res) => {
          this.categories = res['responseResult'];
          this.topics = [];
        },
      })
    );
  }

  getAllSubCategories(event: CategoryResponse) {
    this.isFetchingSubcategories = true;

    this.sub.add(
      this._course
        .getAllSubcategories(event.id)
        .pipe(finalize(() => (this.isFetchingSubcategories = false)))
        .subscribe({
          next: (res) => {
            this.subCategories = res['responseResult'];
          },
        })
    );
  }

  getAllTopics(event) {
    this.sub.add(
      this._course.getllTopics(event.id).subscribe({
        next: (res) => {
          this.topics = res['responseResult'];
        },
      })
    );
  }
  finish() {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This course will be saved and you will be taken to your courses',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1CB4AE',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
    }).then((result) => {
      if (result.value) {
        this._global.showSpinner();
        const isUpdate = !!this.courseData;
        this.saveCourse(null, isUpdate);
      }
    });
  }

  addLesson(route: string) {
    const isUpdate = !!this.courseData;

    // If it's an update and the form is not dirty, navigate based on route
    if (isUpdate && !this.createCourseForm.dirty) {
      if (route) {
        this.router.navigate(['courses/edit-lesson', this.courseCode]);
      } else {
        this.router.navigate(['courses/course-details', this.courseCode]);
      }
      return;
    }

    // Check if the form is valid
    if (this.createCourseForm.invalid) {
      this.createCourseForm.markAllAsTouched();
      return;
    }

    this.saveCourse(route, isUpdate);
  }

  saveCourse(route?, isUpdate?: boolean) {
    this.isLoading = true;

    this.topicsObject();

    const payload = this.createCourseForm.value;
    if (!isUpdate) {
      payload.courseType = 'FREE';
    } else {
      this.createCourseForm.patchValue({ topics: this.topicsObjectArray });
    }
    const { id, ...courseCodeWithoutId } = payload;
    const courseJson = JSON.stringify(courseCodeWithoutId);
    const certificate = this.createCourseForm.get('certificate').value;
    const picture = this.createCourseForm.get('picture').value;

    const apiCall = isUpdate
      ? this._course.updateCourse(
        certificate,
        this.courseCode,
        courseJson,
        picture
      )
      : this._course.createCourse(certificate, courseJson, picture);

    this.sub.add(
      apiCall.subscribe({
        next: (res: ResponseStructure) => {
          this.isLoading = false;

          if (!isUpdate) {
            this.courseData = res['responseResult'];
            this.courseCode = res['responseResult']?.code;
          }

          if (route) {
            this.router.navigate([
              isUpdate ? 'courses/edit-lesson' : 'courses/add-lesson',
              this.courseData['code'],
            ]);
          } else {
            this._global.hideSpinner();
            isUpdate ? 
              this.router.navigate([
                'courses/course-details',
                this.courseCode,
              ]) : 
              this.router.navigate([
                'courses'
              ]);
          }
        },
        error: (e) => {
          this.isLoading = false;
          this._global.hideSpinner();
        },
      })
    );
  }

  // parseToDate(duration: string) {
  //   const strArr = duration.split(':');
  //   this.courseDuration = {
  //     hour: parseInt(strArr[0]),
  //     minute: parseInt(strArr[1]),
  //     second: 0,
  //   };
  // }

  getFileName(imageUrl) {
    let fileNameLowerCase = imageUrl.split('/').pop().toLowerCase();
    this.fileName = fileNameLowerCase;
  }

  topicsObject() {
    const formTopic: any[] = this.createCourseForm.get('topics').value;
    this.topics.forEach((topic) => {
      formTopic.includes(topic.id), this.topicsObjectArray.push(topic);
    });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
